/**
 * SearchResults — Renders search result mini-cards as a horizontal row in the chat thread.
 *
 * Displays patient and document results from Discovery Engine global search
 * as clickable mini-cards that navigate to the relevant patient or document page.
 * Cards wrap across rows and retain all returned source references.
 *
 * When collapsed=true, cards are hidden behind a "Sources (N)" toggle.
 *
 * See: docs/features/infrastructure/ai-chat.md (Search Result Mini-Cards)
 */
import React, { useState } from "react";
import { User, FileText, ChevronRight, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatEncounterDate } from '../../utils/historyEvidence';

const MAX_TITLE_LENGTH = 40;

const truncate = (str, max) => {
  if (!str || str.length <= max) return str || "";
  return str.slice(0, max - 1) + "\u2026";
};

/**
 * @param {Object} props
 * @param {Array} props.results - Array of search result objects from the backend
 */
const SearchResults = ({ results }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  if (!results || results.length === 0) return null;

  const visibleResults = results;

  const handleClick = (result) => {
    if (result.type === "document" && result.patientId && result.id) {
      const prefix = `doc_${result.patientId}_`;
      const docId = result.id.startsWith(prefix) ? result.id.slice(prefix.length) : result.id;
      navigate("/document", { state: { docId, patientId: result.patientId } });
    } else if (result.patientId) {
      navigate("/patient", { state: { patientId: result.patientId } });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return formatEncounterDate(dateStr);
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="search-sources">
      <button
        className="search-sources-toggle"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <span>Sources ({visibleResults.length})</span>
      </button>
      {expanded && (
        <div className="search-results-row">
          {visibleResults.map((result, idx) => (
            <button
              key={result.id || idx}
              className={`search-mini-card${result.reviewState ? ' search-mini-card-history' : ''}`}
              onClick={() => handleClick(result)}
              title={result.type === "patient" ? "Open patient" : "Open document"}
            >
              <div className="search-mini-card-title">
                {result.type === "patient" ? <User size={11} /> : <FileText size={11} />}
                <span>
                  {result.type === "patient"
                    ? truncate(result.patientName || "Unknown Patient", MAX_TITLE_LENGTH)
                    : truncate(result.title || result.documentTypeName || "Untitled Document", MAX_TITLE_LENGTH)}
                </span>
              </div>
              {result.type === "document" && (
                <div className="search-mini-card-meta">
                  {result.patientName && <span>{result.patientName}</span>}
                  {result.documentDate && <span>{formatDate(result.documentDate)}</span>}
                  {!result.documentDate && <span>Date unknown</span>}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchResults;
